import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";

import { fetchSingleCompanyAdvanced } from "../../../../../middleware/services/companyApi";
import { showLoader, hideLoader, actionError } from "../../../../../middleware/actions/utilityAction";
import { setAdvanceTabDetails } from "../../../../../middleware/actions/companyAction";
import { TextEditor, TextField } from "../../../../components/CustomFormElements/syncFusionFormFields";
import { ckEditorMinimalConfig } from "../../../../../config/ckEditorConfig";
import { advancedValidationSchema } from "../../../../helper/validationSchema/companyValidationSchema";
import ActionButtons from "./actionButtons";

const initialState = {
  companyAbout: "",
  companyWhatsUnique: "",
  companyBusinessModel: "",
  companyFuturePlans: "",
};

const ACCORDION_FIELDS = [
  {
    key: "aboutTheCompany",
    label: "About The Company",
    type: "editor",
    field: "companyAbout",
    placeholder: "About The Company",
    required: true,
  },
  {
    key: "companyWhatsUnique",
    label: "Company What's Unique/Tagline",
    type: "textfield",
    field: "companyWhatsUnique",
    placeholder: "Company What's Unique/Tagline",
    maxLength: 200,
  },
  {
    key: "companyBusinessModel",
    label: "Company Business Model",
    type: "editor",
    field: "companyBusinessModel",
    placeholder: "Company Business Model",
  },
  {
    key: "companyFuturePlans",
    label: "Future Plans",
    type: "editor",
    field: "companyFuturePlans",
    placeholder: "Future Plans",
  },
];

const accordionStyles = {
  backgroundColor: "white",
  margin: "0 !important",
  borderTop: "1px solid #e0e0e0",
  borderBottom: "none",
  borderLeft: "none",
  borderRight: "none",
  borderRadius: "0 !important",
  boxShadow: "none",
  "&.Mui-expanded": {
    margin: "0 !important",
    borderTop: "1px solid #e0e0e0",
  },
  "&:before": {
    display: "none",
  },
};

const accordionSummaryStyles = {
  flexDirection: "row-reverse",
  "&.Mui-expanded": {
    margin: 0,
    minHeight: "48px",
  },
  "& .MuiAccordionSummary-content": {
    flexGrow: 1,
    margin: 0,
    alignItems: "center",
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    marginLeft: "auto",
  },
  height: "40px",
};

const AdvancedUI = ({
  companyId,
  accessRights,
  selectedIndex,
  setAdvancedFetched,
  advancedFetched,
  handleCommonSave,
  setCurrentTab,
  path,
  hashArr,
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const dispatch = useDispatch();

  const commonAdvance = useSelector((state) => state.companyState?.advanceDetails);

  const formik = useFormik({
    initialValues: initialState,
    validationSchema: Yup.object().shape(advancedValidationSchema),
  });

  const handleAccordionChange = (panel) =>
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: !prev[panel],
    }));

  const getCompanyDetails = useCallback(() => {
    if (!companyId) return;
    dispatch(showLoader());

    const params = {
      fields: ACCORDION_FIELDS.map((field) => field.field),
    };

    fetchSingleCompanyAdvanced(companyId, params)
      .then((response) => {
        const data = response?.data?.data || {};
        setAdvancedFetched(true);

        formik.setValues({
          companyAbout: advancedFetched ? commonAdvance?.companyAbout : data.companyAbout,
          companyWhatsUnique: advancedFetched ? commonAdvance?.companyWhatsUnique : data.companyWhatsUnique,
          companyBusinessModel: advancedFetched ? commonAdvance?.companyBusinessModel : data.companyBusinessModel,
          companyFuturePlans: advancedFetched ? commonAdvance?.companyFuturePlans : data.companyFuturePlans,
          id: commonAdvance?.id || data.id,
        });
      })
      .catch((err) => dispatch(actionError(err?.data?.message || "Failed to fetch company advanced info")))
      .finally(() => dispatch(hideLoader()));
  }, [companyId, dispatch, advancedFetched, commonAdvance, setAdvancedFetched]);

  useEffect(() => {
    getCompanyDetails();
  }, []);

  useEffect(() => {
    dispatch(setAdvanceTabDetails(formik.values));
  }, [formik.values]);

  const renderAccordion = ({ key, label, type, field, placeholder, required, maxLength }) => (
    <Accordion key={key} sx={accordionStyles} expanded={!!expandedAccordions[key]} onChange={() => handleAccordionChange(key)}>
      <AccordionSummary sx={accordionSummaryStyles} aria-controls={`${key}-content`} id={`${key}-header`}>
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <CheckCircleIcon
            sx={{
              color: formik.values[field]?.length > 0 ? "#0CB963" : "action.disabled",
              fontSize: "28px",
            }}
          />
          <Typography variant="h3" fontWeight={700} fontSize="16px" className="pl-2">
            {label}
          </Typography>
        </Box>
        <Button variant="text" className="!bg-transparent !p-0" onClick={() => handleAccordionChange(key)}>
          {expandedAccordions[key] ? (
            <KeyboardArrowUpOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
          ) : (
            <KeyboardArrowDownOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
          )}
        </Button>
      </AccordionSummary>
      {expandedAccordions[key] && (
        <AccordionDetails sx={{ "& .form-group": { marginBottom: "0 !important" } }}>
          {type === "editor" ? (
            <TextEditor
              formik={formik}
              properties={{
                id: field,
                name: field,
                activeClass: "editor",
                label: required ? "Description *" : "Description",
                config: {
                  ...ckEditorMinimalConfig,
                  editorplaceholder: placeholder,
                  height: companyId ? "200px" : "206px",
                },
              }}
            />
          ) : (
            <TextField
              style={{ height: "100px" }}
              formik={formik}
              multiline
              numberCount
              rows={1}
              maxLength={maxLength}
              name={field}
              id={field}
              className="form-control"
              placeholder={placeholder}
              characterLimitText
              hideInitial
              value={formik.values?.[field] || ""}
            />
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );

  return (
    <div className="gennx-content-wrapper padding-bottom-100i pt-3">
      <div className="mt-0 mx-3">
        <div className="h-full flex flex-col w-auto border border-[#DBDBDD] overflow-y-auto">{ACCORDION_FIELDS.map(renderAccordion)}</div>
      </div>
      <ActionButtons
        accessRights={accessRights}
        selectedIndex={selectedIndex}
        nextPath={companyId ? `/company-old/${companyId}/edit#basic` : true}
        previousPath={`/company-old/${companyId}/edit#basic`}
        handleCommonSave={handleCommonSave}
        setCurrentTab={setCurrentTab}
        back={path === "/processing/company-old/:companyId/edit" ? "/processing/company-old" : "/company-old"}
        companyId={companyId}
        formik={formik}
        hashArr={hashArr}
      />
    </div>
  );
};

AdvancedUI.propTypes = {
  accessRights: PropTypes.arrayOf(PropTypes.string),
  advancedFetched: PropTypes.bool,
  setAdvancedFetched: PropTypes.func,
  selectedIndex: PropTypes.number,
  setCurrentTab: PropTypes.func,
  handleCommonSave: PropTypes.func,
  companyId: PropTypes.string,
  path: PropTypes.string,
  hashArr: PropTypes.array,
};

export default AdvancedUI;
